import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { getPdfViewerUrl, isPdfProofPreview, isRasterProofPreview } from '../../../util/digitalProofPreview.js'
import InlineContentLoader2 from '../../global/loaders/InlineContentLoader2'
import ArtworkPreviewSurface from './ArtworkPreviewSurface'
import WarningIcon from './WarningIcon.js'

type ProofPreview = {
  url: string
  format?: string
  filename?: string
  variant?: string
  page_dimensions?: {
    width?: number | null
    height?: number | null
    unit?: string | null
  } | null
}

type ProofPreviewModalProps = {
  isOpen: boolean
  previewFailed: boolean
  previewLoading: boolean
  previewErrorMessage?: string | null
  proofPreview?: ProofPreview | null
  isGeneratingProof: boolean
  isAddingToCart: boolean
  proof1: boolean
  proof2: boolean
  isBundleProduct?: boolean
  isSocialProduct?: boolean
  portal: {
    digital_proofing_text_1?: string
    digital_proofing_text_2?: string
  }
  onCancel: () => void
  onDownloadProofPreview: () => void
  onRetry: () => void
  onToggleProof: (proofNumber: 1 | 2) => void
  onApprove: () => void
  onPublish?: () => void
}

const DEFAULT_ERROR_TEXT =
  "We encountered an issue while loading your proof. Please try refreshing the page or click 'Retry'. If the problem persists,"

const ProofPreviewModal = ({
  isOpen,
  previewFailed,
  previewLoading,
  previewErrorMessage,
  proofPreview,
  isGeneratingProof,
  isAddingToCart,
  proof1,
  proof2,
  isBundleProduct,
  isSocialProduct,
  portal,
  onCancel,
  onDownloadProofPreview,
  onRetry,
  onToggleProof,
  onApprove,
  onPublish
}: ProofPreviewModalProps) => {
  const errorText = previewErrorMessage || DEFAULT_ERROR_TEXT
  const previewContainerRef = useRef<HTMLDivElement | null>(null)
  const [previewContainerWidth, setPreviewContainerWidth] = useState<number | null>(null)

  useEffect(() => {
    if (!isOpen || !previewContainerRef.current) return undefined

    const element = previewContainerRef.current
    const updatePreviewWidth = () => setPreviewContainerWidth(element.clientWidth || null)
    updatePreviewWidth()

    // Re-measure after modal layout changes so native PDF fragments use the current preview width.
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updatePreviewWidth)
      return () => window.removeEventListener('resize', updatePreviewWidth)
    }

    const observer = new ResizeObserver(updatePreviewWidth)
    observer.observe(element)
    return () => observer.disconnect()
  }, [isOpen, proofPreview?.url])

  return (
    <Modal isOpen={isOpen} className="preview-modal">
      <ModalHeader
        close={
          <button className="close" onClick={onCancel} type="button">
            &times;
          </button>
        }
      >
        {previewFailed ? 'Error Loading Proof' : 'Proof Preview'}
      </ModalHeader>

      <ModalBody>
        {proofPreview?.url ? (
          <div ref={previewContainerRef}>
            {isPdfProofPreview(proofPreview) ? (
              <object
                width="100%"
                height="100vh"
                style={{ minHeight: '500px' }}
                type="application/pdf"
                data={getPdfViewerUrl(proofPreview.url, {
                  preview: proofPreview,
                  containerWidth: previewContainerWidth
                })}
              >
                <p className="d-inline align-items-center justify-content-center lead">{errorText}</p>{' '}
                <a href="/contact-us" className="lead link-underline-primary">
                  Contact Support
                </a>
              </object>
            ) : isRasterProofPreview(proofPreview) ? (
              <ArtworkPreviewSurface
                src={proofPreview.url}
                alt="Digital proof preview"
                className="artwork-preview-surface--proof-preview"
                maxHeight="70vh"
              />
            ) : (
              <div className="mf-mb-4">
                <p>Unable to display this proof format. You can download it instead.</p>
                <Button color="success" onClick={onDownloadProofPreview}>
                  Click Here To Download
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="mf-text-center">
            {previewFailed ? (
              <>
                <div>
                  <WarningIcon />
                </div>
                <p className="d-inline align-items-center justify-content-center lead">{errorText}</p>{' '}
                <a href="/contact-us" className="lead link-underline-primary">
                  Contact Support
                </a>
              </>
            ) : (
              <>
                <strong>
                  {isGeneratingProof
                    ? 'Generating proof...'
                    : 'Some Previews May Take Up to a Minute to Load Due to File Size.'}
                </strong>
                <InlineContentLoader2 />
              </>
            )}
          </div>
        )}

        {!previewFailed && (
          <>
            <FormGroup
              check
              style={{
                display: 'flex',
                alignItems: 'baseline',
                flexDirection: 'row',
                alignContent: 'baseline'
              }}
            >
              <Label check>
                <Input
                  type="checkbox"
                  name="proof1"
                  id="proof1"
                  value={String(proof1)}
                  onChange={() => onToggleProof(1)}
                  style={{ index: '20' } as CSSProperties}
                />{' '}
                {portal.digital_proofing_text_1}
              </Label>
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  name="proof2"
                  id="proof2"
                  value={String(proof2)}
                  onChange={() => onToggleProof(2)}
                />{' '}
                {portal.digital_proofing_text_2}
              </Label>
            </FormGroup>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        {!previewFailed && (
          <Button className="btn mf-outline-btn" onClick={onCancel}>
            Cancel
          </Button>
        )}

        {proof1 && proof2 && !previewFailed && (
          <Button
            className="btn mf-primary-btn"
            disabled={previewLoading || previewFailed || isAddingToCart}
            onClick={isSocialProduct ? onPublish : onApprove}
          >
            {isSocialProduct ? 'Publish to Social' : isBundleProduct ? 'Approve Proof' : 'Add to Cart'}
          </Button>
        )}
        {previewFailed && (
          <Button className="btn mf-primary-btn" disabled={false} onClick={onRetry}>
            Retry
          </Button>
        )}
      </ModalFooter>
    </Modal>
  )
}

export default ProofPreviewModal
